import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Modal } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { IContaBancaria, IFechamentoConta, IFechamentosVisao, IFechamentoVersao } from '../../interfaces/IModuloBancario';
import ModuloBancarioService from '../../services/ModuloBancarioService';

interface Props {
    esteiraId: number;
    /** "AAAA-MM"; vazio = nada a fechar. */
    competencia: string;
    contas: IContaBancaria[];
    /** Muda quando as minutas mudam: os impedimentos dependem delas. */
    versaoMinutas: number;
    /** Exportar, reabrir e conferir mudam a situação das minutas. */
    onAlterou: () => void;
}

const INTERVALO_MS = 5000;

const SITUACAO: Record<string, { texto: string; classe: string }> = {
    ABERTA: { texto: 'Aberta', classe: 'badge-light' },
    REABERTA: { texto: 'Reaberta', classe: 'badge-light-warning' },
    FECHADA: { texto: 'Fechada', classe: 'badge-light-primary' },
    EXPORTADA: { texto: 'Exportada', classe: 'badge-primary' },
};

const RETORNO: Record<string, { texto: string; classe: string }> = {
    ACEITO: { texto: 'Aceito', classe: 'badge-light-success' },
    REJEITADO: { texto: 'Rejeitado', classe: 'badge-light-danger' },
    PARCIAL: { texto: 'Parcial', classe: 'badge-light-warning' },
};

type Pedido =
    | { tipo: 'reemitir' | 'reabrir'; conta: IFechamentoConta }
    | { tipo: 'retorno'; conta: IFechamentoConta };

const brl = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
// O backend grava em UTC sem o "Z".
const dataHora = (valor: string | null) => (valor ? moment.utc(valor).local().format('DD/MM/YYYY HH:mm') : '—');
const descreverConta = (c?: IContaBancaria) =>
    c ? `${c.banco} / ${c.agencia ? `${c.agencia} / ` : ''}${c.conta}${c.descricao ? ` — ${c.descricao}` : ''}` : '—';

function Diferenca({ json }: { json: string | null }) {
    if (!json) return null;
    let dados: { Entraram: any[]; Sairam: any[] };
    try {
        dados = JSON.parse(json);
    } catch {
        return null;
    }
    if (dados.Entraram.length === 0 && dados.Sairam.length === 0)
        return <div className='text-muted fs-8'>Sem diferença para a versão anterior.</div>;

    const linha = (l: any, sinal: string, i: number) => (
        <div key={`${sinal}${i}`} className={sinal === '+' ? 'text-success' : 'text-danger'}>
            {sinal} {moment(l.Data).format('DD/MM/YYYY')} · D {l.Debito} · C {l.Credito} · {brl(l.Valor)} · {l.Historico}
        </div>
    );
    return (
        <div className='fs-8'>
            {dados.Entraram.map((l, i) => linha(l, '+', i))}
            {dados.Sairam.map((l, i) => linha(l, '-', i))}
        </div>
    );
}

// DEVN3-771 — fechamento por conta bancária × competência, exportação para o Domínio, reabertura e retorno.
export default function FechamentoBancario({ esteiraId, competencia, contas, versaoMinutas, onAlterou }: Props) {
    const [visao, setVisao] = useState<IFechamentosVisao>({ podeReabrir: false, conferenciaEmAndamento: false, contas: [] });
    const [carregando, setCarregando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [pedido, setPedido] = useState<Pedido | null>(null);
    const [texto, setTexto] = useState('');
    const [retorno, setRetorno] = useState('ACEITO');
    const [versoes, setVersoes] = useState<IFechamentoConta | null>(null);

    const [ano, mes] = competencia ? competencia.split('-').map(Number) : [0, 0];

    const buscar = useCallback(async (silencioso = false) => {
        if (!ano || !mes) return;
        if (!silencioso) setCarregando(true);
        try {
            setVisao(await ModuloBancarioService.fechamentos(esteiraId, ano, mes));
        } catch (err: any) {
            if (!silencioso) Swal.fire({ text: err?.message ?? 'Não foi possível carregar o fechamento.', icon: 'error' });
        } finally {
            if (!silencioso) setCarregando(false);
        }
    }, [esteiraId, ano, mes]);

    useEffect(() => {
        if (!competencia) {
            setVisao({ podeReabrir: false, conferenciaEmAndamento: false, contas: [] });
            return;
        }
        buscar();
    }, [buscar, competencia, versaoMinutas]);

    useEffect(() => {
        if (!visao.conferenciaEmAndamento) return;
        const id = setInterval(() => buscar(true), INTERVALO_MS);
        return () => {
            clearInterval(id);
            onAlterou();
        };
    }, [visao.conferenciaEmAndamento, buscar, onAlterou]);

    const executar = async (acao: () => Promise<unknown>, erro: string) => {
        setSalvando(true);
        try {
            await acao();
            await buscar(true);
            onAlterou();
            return true;
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? erro, icon: 'error' });
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const exportar = async (conta: IFechamentoConta) => {
        const confirmacao = await Swal.fire({
            title: 'Exportar para o Domínio?',
            text: `${conta.minutasAprovadas} minuta(s), ${brl(conta.valorAprovado)}. Depois de exportar, a conta fica bloqueada nesta competência e só o Administrador reabre.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Exportar',
            cancelButtonText: 'Cancelar',
        });
        if (!confirmacao.isConfirmed) return;
        await executar(() => ModuloBancarioService.exportarFechamento(esteiraId, conta.contaBancariaId, ano, mes), 'Não foi possível exportar.');
    };

    const baixar = (v: IFechamentoVersao) =>
        ModuloBancarioService.baixarVersao(esteiraId, v.id, `dominio_${competencia}_v${v.versao}.txt`)
            .catch((err: any) => Swal.fire({ text: err?.message ?? 'Não foi possível baixar o arquivo.', icon: 'error' }));

    const conferir = () =>
        executar(() => ModuloBancarioService.conferirRazao(esteiraId, false), 'Não foi possível iniciar a conferência.');

    const abrirPedido = (novo: Pedido) => {
        setTexto('');
        setRetorno('ACEITO');
        setPedido(novo);
    };

    const confirmarPedido = async () => {
        if (!pedido) return;
        const id = pedido.conta.contaBancariaId;
        const ok = await executar(() => {
            if (pedido.tipo === 'reemitir') return ModuloBancarioService.reemitirFechamento(esteiraId, id, ano, mes, texto);
            if (pedido.tipo === 'reabrir') return ModuloBancarioService.reabrirFechamento(esteiraId, id, ano, mes, texto);
            return ModuloBancarioService.informarRetorno(esteiraId, id, ano, mes, retorno, texto);
        }, 'Não foi possível concluir.');
        if (ok) setPedido(null);
    };

    return (
        <div className='card card-flush mb-4'>
            <div className='card-header align-items-center'>
                <h3 className='card-title fw-bold'>
                    Fechamento e exportação
                    {(carregando || visao.conferenciaEmAndamento) && <span className='spinner-border spinner-border-sm ms-3'></span>}
                </h3>
                {competencia && (
                    <button className='btn btn-sm btn-light' disabled={salvando || visao.conferenciaEmAndamento} onClick={conferir}
                        title='Confere as minutas exportadas contra o razão da esteira ativa'>
                        {visao.conferenciaEmAndamento ? 'Conferindo o razão…' : 'Conferir razão'}
                    </button>
                )}
            </div>
            <div className='card-body pt-0'>
                {!competencia ? (
                    <div className='text-muted'>Escolha a competência no topo da página.</div>
                ) : visao.contas.length === 0 ? (
                    !carregando && <div className='text-muted'>Nenhuma conta bancária ativa.</div>
                ) : (
                    <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                        <thead>
                            <tr className='fw-bold text-muted'>
                                <th>Conta bancária</th>
                                <th>Situação</th>
                                <th>Arquivo</th>
                                <th className='text-end'>Minutas</th>
                                <th className='text-end'>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visao.contas.map(c => {
                                const situacao = SITUACAO[c.situacao] ?? SITUACAO.ABERTA;
                                const ultima = c.versoes[0];
                                const podeFechar = (c.situacao === 'ABERTA' || c.situacao === 'REABERTA') && c.impedimentos.length === 0;
                                return (
                                    <tr key={c.contaBancariaId}>
                                        <td className='fs-7'>{descreverConta(contas.find(x => x.id === c.contaBancariaId))}</td>
                                        <td style={{ maxWidth: 320 }}>
                                            <span className={`badge ${situacao.classe}`}>{situacao.texto}</span>
                                            {c.impedimentos.map(i => <div key={i} className='text-muted fs-8 mt-1'>{i}</div>)}
                                            {c.situacao === 'REABERTA' && c.motivoReabertura && <div className='text-muted fs-8 mt-1'>Motivo: {c.motivoReabertura}</div>}
                                            {c.alertaAceitoNaoVeio && (
                                                <div className='text-danger fs-8 mt-1'>
                                                    Retorno informado como aceito, mas o razão reimportado não trouxe {c.minutasExportadas} minuta(s).
                                                </div>
                                            )}
                                        </td>
                                        <td className='fs-7'>
                                            {!ultima ? '—' : (
                                                <>
                                                    <button className='btn btn-link btn-sm p-0' onClick={() => setVersoes(c)}>Versão {ultima.versao}</button>
                                                    {ultima.retorno
                                                        ? <span className={`badge ms-2 ${RETORNO[ultima.retorno]?.classe}`}>{RETORNO[ultima.retorno]?.texto}</span>
                                                        : <span className='badge badge-light-info ms-2'>Arquivo gerado</span>}
                                                </>
                                            )}
                                        </td>
                                        <td className='text-end fs-7 text-nowrap'>
                                            {c.minutasAprovadas > 0 && <div>{c.minutasAprovadas} aprovada(s) · {brl(c.valorAprovado)}</div>}
                                            {c.minutasExportadas > 0 && <div>{c.minutasExportadas} exportada(s)</div>}
                                            {c.minutasContabilizadas > 0 && <div className='text-success'>{c.minutasContabilizadas} contabilizada(s)</div>}
                                        </td>
                                        <td className='text-end'>
                                            <div className='d-flex flex-wrap gap-1 justify-content-end'>
                                                {(c.situacao === 'ABERTA' || c.situacao === 'REABERTA') && (
                                                    <button className='btn btn-sm btn-light-primary' disabled={salvando || !podeFechar}
                                                        onClick={() => executar(() => ModuloBancarioService.fecharConta(esteiraId, c.contaBancariaId, ano, mes), 'Não foi possível fechar.')}>
                                                        Fechar
                                                    </button>
                                                )}
                                                {c.situacao === 'FECHADA' && (
                                                    <>
                                                        <button className='btn btn-sm btn-primary' disabled={salvando || c.minutasAprovadas === 0} onClick={() => exportar(c)}>Exportar</button>
                                                        <button className='btn btn-sm btn-light' disabled={salvando}
                                                            onClick={() => executar(() => ModuloBancarioService.desfazerFechamento(esteiraId, c.contaBancariaId, ano, mes), 'Não foi possível desfazer.')}>
                                                            Desfazer
                                                        </button>
                                                    </>
                                                )}
                                                {c.situacao === 'EXPORTADA' && (
                                                    <>
                                                        <button className='btn btn-sm btn-light-primary' onClick={() => baixar(ultima)}>Baixar</button>
                                                        <button className='btn btn-sm btn-light' onClick={() => abrirPedido({ tipo: 'retorno', conta: c })}>Retorno</button>
                                                        <button className='btn btn-sm btn-light' disabled={c.minutasExportadas === 0} onClick={() => abrirPedido({ tipo: 'reemitir', conta: c })}>Reemitir</button>
                                                        {visao.podeReabrir && (
                                                            <button className='btn btn-sm btn-light-danger' onClick={() => abrirPedido({ tipo: 'reabrir', conta: c })}>Reabrir</button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {pedido && (
                <Modal show onHide={() => setPedido(null)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {pedido.tipo === 'reemitir' ? 'Reemitir arquivo' : pedido.tipo === 'reabrir' ? 'Reabrir conta bancária' : 'Retorno do Domínio'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {pedido.tipo === 'reabrir' && (
                            <div className='text-muted fs-7 mb-3'>
                                Reabre a conta inteira nesta competência. As minutas exportadas voltam a Aprovadas, as contabilizadas não mudam e as versões anteriores continuam guardadas. A próxima exportação gera uma nova versão.
                            </div>
                        )}
                        {pedido.tipo === 'reemitir' && (
                            <div className='text-muted fs-7 mb-3'>Gera uma nova versão do arquivo e mostra a diferença para a anterior.</div>
                        )}
                        {pedido.tipo === 'retorno' && (
                            <select className='form-select mb-3' value={retorno} onChange={e => setRetorno(e.target.value)}>
                                <option value='ACEITO'>Aceito</option>
                                <option value='PARCIAL'>Parcial</option>
                                <option value='REJEITADO'>Rejeitado</option>
                            </select>
                        )}
                        <label className='form-label'>
                            {pedido.tipo === 'reemitir' ? 'Justificativa' : pedido.tipo === 'reabrir' ? 'Motivo' : `Críticas${retorno === 'ACEITO' ? ' (opcional)' : ''}`}
                        </label>
                        <textarea className='form-control' rows={3} maxLength={pedido.tipo === 'retorno' ? 2000 : 500} value={texto} onChange={e => setTexto(e.target.value)}></textarea>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className='btn btn-light' onClick={() => setPedido(null)}>Cancelar</button>
                        <button className='btn btn-primary' disabled={salvando || (!texto.trim() && (pedido.tipo !== 'retorno' || retorno !== 'ACEITO'))} onClick={confirmarPedido}>
                            Confirmar
                        </button>
                    </Modal.Footer>
                </Modal>
            )}

            {versoes && (
                <Modal show onHide={() => setVersoes(null)} centered size='lg'>
                    <Modal.Header closeButton>
                        <Modal.Title>Versões exportadas</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <table className='table table-sm align-middle fs-7'>
                            <thead>
                                <tr className='fw-bold text-muted'>
                                    <th>Versão</th>
                                    <th>Data e responsável</th>
                                    <th className='text-end'>Totais</th>
                                    <th>Retorno</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {versoes.versoes.map(v => (
                                    <tr key={v.id}>
                                        <td>{v.versao}</td>
                                        <td>
                                            {dataHora(v.dataExportacao)} · {v.usuarioNome ?? '—'}
                                            {v.justificativa && <div className='text-muted fs-8'>{v.justificativa}</div>}
                                            <Diferenca json={v.diferencaJson} />
                                        </td>
                                        <td className='text-end text-nowrap'>{v.quantidade} lanç. · {brl(v.total)}</td>
                                        <td>
                                            {v.retorno ? RETORNO[v.retorno]?.texto : 'Arquivo gerado'}
                                            {v.retornoCriticas && <div className='text-muted fs-8'>{v.retornoCriticas}</div>}
                                        </td>
                                        <td className='text-end'><button className='btn btn-sm btn-light' onClick={() => baixar(v)}>Baixar</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
}
